import React, { useState, useEffect, useRef, ReactElement, Suspense, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import Notification from '@Common/Notification';
import { DashboardsTab } from '../../styles/shared-styles';
import Openings from './components/Openings/Openings';
import OverviewFilterControls, { OverviewFilterControlsHandle } from './components/Filters/OverviewFilterControls';
import ViewsPanel from './components/Views/ViewsPanel';
import ProjectScopeBar from './components/ProjectScopeBar/ProjectScopeBar';
import TodaysFocus from './components/TodaysFocus/TodaysFocus';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import InputText from '@Common/InputText';
import Modal61 from '@Common/Modal/Modal61';
import ErrorFallback from '../Common/ErrorFallback';
import { isCustomerAdminOrOwner, isRegularUser } from '../../helpers/accountHelper';
import { CORPORATE_DASHBOARD_DEFAULT, OVERVIEW_DEFAULT_TABS } from '../../constants/constants';
import { Modal61Ref } from '@Types/common';
import { Tab, clampDisabledSortBy } from './components/types';
import ConfigDrawer from './Modals/ConfigDrawer';
import { ScoreInfoModalProvider } from './components/ScoreInfo/ScoreInfoModal';
import {
    useLoadDashboardFilters,
    useLoadDashboardSettings,
    useGetOverviewTabsConfig,
    useSaveSettings,
    useUpdateSettings,
    useDeleteSettings,
    useUpdateOverviewTabsConfig,
    useSetOverviewTab,
    DashboardSetting,
    SortByValue,
    SortDirValue,
} from '../../apis/companyDashboard';
import { useLoadProjectListFilters } from '../../apis/project';
import './Overview.scss';

const { CORPORATE_DASHBOARD_VIEW_DEFAULT_ID } = CORPORATE_DASHBOARD_DEFAULT;

export interface ProjectOption {
    id: number;
    name: string;
    abbreviation: string;
    description: string;
}

const defaultSettings = {
    id: undefined as string | undefined,
    name: undefined as string | undefined,
    updates: 0, //this is used to track state changes from customize widget drawer so child components can know when to update without a deep compare
    isProjectSummariesActive: true,
    isLocationSummariesActive: true,
    isMyTasksDueSoonActive: true,
    isMilestonesMissedActive: true,
    brandIds: [],
    areaIds: [],
    regionIds: [],
    managementCompanyIds: [],
    locationTypeIds: [],
    continentIds: [],
    isMilestonesOnly: false,
    disciplineIds: [],
    departmentIds: [],
    phaseIds: [],
    communityIds: [],
    baselineIds: [],
    projectIds: [],
    locationIds: [],
    tagIds: [],
    sortBy: 'name' as SortByValue,
    sortDir: 'asc' as SortDirValue,
};

// Openings tab is built but not yet released — only Today's Focus ships in this beta.
const defaultTabsConfig: Array<Tab> = [
    {
        id: OVERVIEW_DEFAULT_TABS.TodaysFocus.id,
        label: OVERVIEW_DEFAULT_TABS.TodaysFocus.label,
        icon: OVERVIEW_DEFAULT_TABS.TodaysFocus.icon,
        selectedProjectIds: [],
        projectScope: 'all',
        projectNames: [],
    },
    // {
    //     id: OVERVIEW_DEFAULT_TABS.Openings.id,
    //     label: OVERVIEW_DEFAULT_TABS.Openings.label,
    //     icon: OVERVIEW_DEFAULT_TABS.Openings.icon,
    //     selectedProjectIds: [],
    // },
];

const describeProjectScope = (ids: number[], activeProjects: Array<{ id: number; name: string }>): Pick<Tab, 'projectScope' | 'projectNames'> => {
    if (ids.length === 0) return { projectScope: 'all', projectNames: [] };
    const namesById = new Map(activeProjects.map((p) => [p.id, p.name]));
    return { projectScope: 'selected', projectNames: ids.flatMap((id) => (namesById.has(id) ? [namesById.get(id) as string] : [])) };
};

const Overview = (): ReactElement => {
    const [tabs, setTabs] = useState<Array<Tab>>([]);
    const [activeTab, setActiveTab] = useState<Tab>(defaultTabsConfig[0]);
    const [selectedSettings, setSelectedSettings] = useState<DashboardSetting>(defaultSettings);
    const [selectedSettingsId, setSelectedSettingsId] = useState<string>('defaultId');
    const [isSaving, setIsSaving] = useState(false);

    const [isConfigDrawerOpen, setIsConfigDrawerOpen] = useState(false);
    // The blank placeholder tab handed to ConfigDrawer while creating a new tab. Captured once,
    // at open time, into state (rather than a plain render-time const) so its object identity
    // stays stable across unrelated Overview re-renders (e.g. a background query refetch) —
    // ConfigDrawer re-syncs its form fields from `tab` whenever that reference changes, so a
    // fresh object on every render would silently wipe whatever the user had typed so far.
    const [newTabDraft, setNewTabDraft] = useState<Tab | null>(null);
    const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
    const filterControlsRef = useRef<OverviewFilterControlsHandle>(null);
    const saveFiltersModalRef = useRef<Modal61Ref>(null);
    const { customerRole, currentSubscriptionId } = useSelector((state: any) => state.user);

    // ---- TanStack Query — all three initial fetches run in parallel via Suspense ----
    const { data: filtersData } = useLoadDashboardFilters();
    const { data: settingsData } = useLoadDashboardSettings();
    const { data: tabsConfigData } = useGetOverviewTabsConfig();
    const isAdmin = isCustomerAdminOrOwner(customerRole);
    // project/list-filters is admin-only on the API (Owner/Moderator/Admin; 400 for regular users). Everyone
    // else gets their visible projects from overview-tabs-config, which the API scopes to their locations.
    const hasProjectListFiltersAccess = !isRegularUser(customerRole);
    const { data: projectFiltersData } = useLoadProjectListFilters(currentSubscriptionId?.toString() ?? '', hasProjectListFiltersAccess);

    // ---- Mutations ----
    const saveSettingsMutation = useSaveSettings(() => {
        Notification('success', 'Settings Updated and Saved', '');
        saveFiltersModalRef.current?.hide();
    });
    const updateSettingsMutation = useUpdateSettings(() => {
        Notification('success', 'Settings Updated and Saved', '');
        saveFiltersModalRef.current?.hide();
    });
    const deleteSettingsMutation = useDeleteSettings();
    const updateTabsConfigMutation = useUpdateOverviewTabsConfig();
    const setOverviewTabMutation = useSetOverviewTab();

    // ---- Derived data ----
    const availableProjects = hasProjectListFiltersAccess
        ? projectFiltersData?.projectOptionsDropdownList?.map((p) => p.option) ?? []
        : tabsConfigData.availableProjects;

    // ---- Build tab component ----
    // Called at render time (not cached in tabs state) so dashboardSettings changes reach
    // children immediately. Keys intentionally exclude `settings` — including it would remount
    // the child on every filter change and wipe its local state; TanStack query-key changes
    // (inside TodaysFocus/Openings) already handle refetching when settings change.
    // Same Task Filters dialog the toolbar's own "Filters" button opens — the breakdown panel's
    // "filters" link and row-count nudge reuse it via the imperative handle.
    const onOpenTaskFilters = useCallback(() => filterControlsRef.current?.openTaskFilters(), []);
    // Sort selection lives in selectedSettings, so Save/Update View persists it and selecting a
    // saved view restores it. Stable identity so it never remounts Openings.
    const onSortChange = useCallback((sortBy: SortByValue, sortDir: SortDirValue) => {
        setSelectedSettings((prev) => ({ ...prev, sortBy, sortDir }));
    }, []);

    const buildTabComponent = useCallback((tab: Tab, settings: DashboardSetting) => {
        // Today's Focus (id 1) is the only fixed, non-configurable tab. Every other tab — the
        // original "Tab1" and any tab created via "+ New Tab" — is a generic configurable tab and
        // renders the same Openings-style content, scoped to its own projects/sections.
        if (tab.id === OVERVIEW_DEFAULT_TABS.TodaysFocus.id) {
            const ids = Array.isArray(tab.selectedProjectIds) ? [...tab.selectedProjectIds] : [];
            return <TodaysFocus key={`todays-focus-${tab.id}-${ids.join('-')}`} selectedProjectIds={ids} dashboardSettings={settings} />;
        }

        const ids = Array.isArray(tab.selectedProjectIds) ? [...tab.selectedProjectIds] : [];
        const sectionIds = Array.isArray(tab.selectedSectionIds) ? [...tab.selectedSectionIds] : [];
        return (
            <Openings
                key={`openings-${tab.id}-${ids.join('-')}-${sectionIds.join('-')}`}
                tabId={tab.id}
                projectIds={ids}
                selectedSectionIds={sectionIds}
                dashboardSettings={settings}
                onOpenTaskFilters={onOpenTaskFilters}
                onSortChange={onSortChange}
            />
        );
    }, [onSortChange]);

    // Guards the initial "jump to the server-remembered active tab" below so it only ever
    // fires once. Without this, a later refetch of tabsConfigData (e.g. the invalidation that
    // follows saveConfig) would re-run it and could stomp a tab switch made since — most
    // visibly, "+ New Tab" locally activating the tab it just created, only to be raced back to
    // the previous tab because the separate setOverviewTabMutation persist call hadn't landed
    // server-side yet when the tabs-config refetch resolved.
    const hasAppliedInitialActiveTabRef = useRef(false);

    // ---- Initialise tabs from server config (runs once when data arrives) ----
    useEffect(() => {

        const serverTabs = tabsConfigData.overviewConfig.tabs;

        // Today's Focus (id 1) is mandatory, non-configurable, and always first — its label/icon
        // must never be overridden by saved server config. Every other entry in the server's tab
        // list becomes a tab here, not just the original default second tab: this is what lets
        // tabs created via "+ New Tab" show up (and survive a refresh).
        const tabsConfig: Array<Tab> = serverTabs && serverTabs.length > 0
            ? [
                defaultTabsConfig[0],
                ...serverTabs
                    .filter((t) => t.id !== OVERVIEW_DEFAULT_TABS.TodaysFocus.id)
                    .map((saved) => ({
                        id: saved.id,
                        label: saved.label,
                        icon: OVERVIEW_DEFAULT_TABS.Openings.icon,
                        selectedProjectIds: saved.selectedProjectIds,
                        selectedSectionIds: saved.selectedSectionIds ?? [],
                        projectScope: saved.projectScope,
                        projectNames: saved.projectNames,
                    })),
            ]
            : [...defaultTabsConfig];

        setTabs(tabsConfig);

        if (!hasAppliedInitialActiveTabRef.current) {
            hasAppliedInitialActiveTabRef.current = true;
            if (tabsConfigData.selectedTabId != null) {
                const selected = tabsConfig.find((tab) => tab.id === tabsConfigData.selectedTabId);
                if (selected) setActiveTab(selected);
            }
        }
    }, [tabsConfigData]);

    // Guards the settings-init effect below so it only ever fires once. Without this, a later
    // refetch of settingsData (e.g. the invalidation that follows onDeleteView's mutation) would
    // re-run it and stomp a selection made since — most visibly, onDeleteView's own fallback to
    // the default/unsaved state, only to be raced back to whatever view now sorts first once the
    // background refetch resolves. Same pattern as hasAppliedInitialActiveTabRef above.
    const hasAppliedInitialSettingsRef = useRef(false);

    // ---- Initialise selected settings from server data (runs once) ----
    useEffect(() => {
        if (hasAppliedInitialSettingsRef.current) return;
        if (settingsData.length === 0) return;
        hasAppliedInitialSettingsRef.current = true;
        const first = settingsData[0];
        const firstId = first.id ?? CORPORATE_DASHBOARD_VIEW_DEFAULT_ID;
        setSelectedSettingsId(firstId);
        setSelectedSettings(firstId === CORPORATE_DASHBOARD_VIEW_DEFAULT_ID ? defaultSettings : clampDisabledSortBy(first));
    }, [settingsData]);

    // ---- Handlers ----

    const onTabClick = (tab: Tab) => {
        setOverviewTabMutation.mutate({ overviewTabId: tab.id, openingsSubTabId: undefined });
        setActiveTab(tab);
    };

    const onSettingsMenuChange = (id: string) => {
        const found = settingsData.find((s) => s?.id === id) ?? defaultSettings;
        setSelectedSettings(clampDisabledSortBy(found));
        setSelectedSettingsId(id);
    };

    const onDeleteView = (id: string) => {
        deleteSettingsMutation.mutate(id, {
            onSuccess: () => {
                Notification('success', '', 'View deleted');
                // Deleting the view currently applied falls back to the unsaved/default state —
                // there is nothing left to keep showing.
                if (selectedSettingsId === id) {
                    setSelectedSettings(defaultSettings);
                    setSelectedSettingsId(CORPORATE_DASHBOARD_VIEW_DEFAULT_ID);
                }
            },
            onError: (err: Error) => Notification('error', 'Error', err.message),
        });
    };

    const onSaveCustomFilters = () => {
        const settings = { ...selectedSettings };
        if (settings.id === CORPORATE_DASHBOARD_VIEW_DEFAULT_ID) settings.id = undefined;
        setIsSaving(true);

        const onSettled = () => setIsSaving(false);
        const onError = (err: Error) => Notification('error', 'Error', err.message);

        if (settings.id) {
            updateSettingsMutation.mutate({ settings, id: settings.id }, { onSettled, onError });
        } else {
            saveSettingsMutation.mutate(settings, {
                onSuccess: (data) => {
                    if (data?.id) {
                        setSelectedSettings((prev) => ({ ...prev, id: data.id }));
                        setSelectedSettingsId(data.id);
                    }
                },
                onSettled,
                onError,
            });
        }
    };

    const onCompleteCustomizerDrawer = (newSettings: DashboardSetting) => {
        setSelectedSettings(newSettings);
        setSelectedSettingsId(newSettings.id ?? 'defaultId');
    };

    const saveConfig = (tabId: number, tabLabel: string, selectedProjectIds: number[], selectedSectionIds: string[]): Promise<void> => {
        return new Promise((resolve) => {
            updateTabsConfigMutation.mutate(
                { tabId, tabLabel, selectedProjectIds, selectedSectionIds },
                {
                    onSuccess: () => {
                        Notification('success', 'Settings Updated and Saved', '');

                        const tabsConfig = [...tabs];
                        const tabIndex = tabsConfig.findIndex((t) => t.id === tabId);
                        let updatedTab: Tab;

                        if (tabIndex !== -1) {
                            updatedTab = { ...tabsConfig[tabIndex], label: tabLabel, selectedProjectIds, selectedSectionIds, ...describeProjectScope(selectedProjectIds, availableProjects) };
                            tabsConfig[tabIndex] = updatedTab;
                        } else {
                            // Not found among the current tabs — this is a brand-new tab (the
                            // "+ New Tab" flow), so append it instead of updating in place.
                            updatedTab = { id: tabId, label: tabLabel, icon: OVERVIEW_DEFAULT_TABS.Openings.icon, selectedProjectIds, selectedSectionIds, ...describeProjectScope(selectedProjectIds, availableProjects) };
                            tabsConfig.push(updatedTab);
                        }
                        setTabs(tabsConfig);

                        // Editing the already-active tab needs its fresh copy in state; creating a
                        // new tab always navigates the user onto it so they can see what they made.
                        if (tabIndex !== -1) {
                            if (activeTab?.id === tabId) setActiveTab(updatedTab);
                        } else {
                            setActiveTab(updatedTab);
                            setOverviewTabMutation.mutate({ overviewTabId: updatedTab.id, openingsSubTabId: undefined });
                        }

                        setIsConfigDrawerOpen(false);
                        setNewTabDraft(null);
                        resolve();
                    },
                    onError: (err) => {
                        Notification('error', 'Error', err.message);
                        resolve();
                    },
                }
            );
        });
    };

    const updateView = selectedSettings.id && selectedSettings.id !== CORPORATE_DASHBOARD_VIEW_DEFAULT_ID;

    return (
        <LoadingIndicator isLoading={isSaving} hideWhileLoading={false} isFullscreen={false}>
            <div className='overview'>
                <div className='row g-0 align-items-end mb-4'>
                    <div className='col-12 col-lg-6'>
                        <DashboardsTab className='nav nav-tabs'>
                            {tabs.map((tab) => (
                                <li key={tab.id} className='nav-item'>
                                    <a
                                        className={`nav-link ${activeTab?.id === tab.id ? 'active' : ''}`}
                                        onClick={() => onTabClick(tab)}
                                        type='button'>
                                        <i className={`fa-regular ${tab.icon}`} />
                                        <span>{tab.label}</span>
                                    </a>
                                </li>
                            ))}
                        </DashboardsTab>
                    </div>

                    <div className='col d-flex justify-content-end flex-wrap gap-2 nav nav-tabs pb-2'>
                        {isAdmin && (
                            <button
                                type='button'
                                className='btn'
                                onClick={() => {
                                    // Decided now, at open time — a new admin session opened later
                                    // will see this tab in `tabsConfigData` and compute a higher one.
                                    const id = Math.max(0, ...tabs.map((t) => t.id)) + 1;
                                    setNewTabDraft({ id, label: '', icon: OVERVIEW_DEFAULT_TABS.Openings.icon, selectedProjectIds: [], selectedSectionIds: [] });
                                }}>
                                <i className='fa-regular fa-plus'></i>
                                <span className='ms-2'>New Tab</span>
                            </button>
                        )}

                        {isAdmin && activeTab?.id !== OVERVIEW_DEFAULT_TABS.TodaysFocus.id && (
                            <button type='button' className='btn' onClick={() => setIsConfigDrawerOpen(true)}>
                                <i className='fa-regular fa-pen-to-square'></i>
                                <span className='ms-2'>Edit Tab</span>
                            </button>
                        )}

                        <ViewsPanel
                            settings={settingsData}
                            selectedId={selectedSettingsId}
                            onSelect={onSettingsMenuChange}
                            onSaveCurrent={() => saveFiltersModalRef.current?.show()}
                            onDelete={onDeleteView}
                        />

                        <div className='overview-filters-toolbar'>
                            <div className={`overview-filters-sub-btn-wrap ${isFiltersExpanded ? 'is-expanded' : ''}`}>
                                <button
                                    type='button'
                                    className='btn overview-filters-sub-btn'
                                    tabIndex={isFiltersExpanded ? 0 : -1}
                                    aria-hidden={!isFiltersExpanded}
                                    onClick={() => filterControlsRef.current?.openTaskFilters()}>
                                    <i className='fa-regular fa-list-check'></i>
                                    <span className='ms-2'>Task Filters</span>
                                </button>
                            </div>
                            <div className={`overview-filters-sub-btn-wrap ${isFiltersExpanded ? 'is-expanded' : ''}`}>
                                <button
                                    type='button'
                                    className='btn overview-filters-sub-btn'
                                    tabIndex={isFiltersExpanded ? 0 : -1}
                                    aria-hidden={!isFiltersExpanded}
                                    onClick={() => filterControlsRef.current?.openLocationFilters()}>
                                    <i className='fa-regular fa-location-dot'></i>
                                    <span className='ms-2'>Location Filters</span>
                                </button>
                            </div>
                            <button
                                type='button'
                                className={`btn overview-filters-toggle ${isFiltersExpanded ? 'overview-filters-toggle-active' : ''}`}
                                onClick={() => setIsFiltersExpanded((expanded) => !expanded)}>
                                <i className='fa-regular fa-filter'></i>
                                <span className='ms-2 show-filters'>Filters</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className='row'>
                    <div className='col-12'>
                        {(() => {
                            const activeTabEntry = tabs.find((tab) => tab.id === activeTab?.id);
                            if (!activeTabEntry) return null;
                            const scope = activeTabEntry.projectScope ?? 'all';
                            const names = activeTabEntry.projectNames ?? [];
                            const hasNoActiveProjects = scope === 'selected' && names.length === 0;
                            return (
                                <>
                                    <ProjectScopeBar scope={scope} projectNames={names} />
                                    {hasNoActiveProjects ? (
                                        <div className='overview-empty-state'>There are no active projects to show on this tab.</div>
                                    ) : (
                                        buildTabComponent(activeTabEntry, selectedSettings)
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>

            {/* Modals */}

            <Modal61
                title='Save View'
                ref={saveFiltersModalRef}
                isCentered={true}
                size='md'
                body={
                    <form name='saveViewForm' noValidate>
                        <div className='form-group'>
                            <label htmlFor='custom-filters-name' className='title'>
                                Custom View Name
                            </label>
                            <InputText
                                name='custom-filters-name'
                                type='text'
                                value={selectedSettings.name ?? ''}
                                onChange={(e) => setSelectedSettings((prev) => ({ ...prev, name: e.target.value }))}
                                isRequired={true}
                                errorMessage='Custom View Name is required'
                                maxLength={20}
                                placeholder={updateView ? 'Would you like to rename this view?' : 'What would you like to name this view?'}
                            />
                            <small id='custom-view-name-help' className='form-text text-muted sub-title'>
                                Character Limit 20
                            </small>
                        </div>
                    </form>
                }
                primaryButtonText={updateView ? 'Update' : 'Save'}
                onPrimaryClick={onSaveCustomFilters}
            />

            {isConfigDrawerOpen && (
                <ConfigDrawer
                    tab={activeTab}
                    availableProjects={availableProjects}
                    onSave={saveConfig}
                    onClose={() => setIsConfigDrawerOpen(false)}
                />
            )}

            {newTabDraft && (
                <ConfigDrawer
                    tab={newTabDraft}
                    availableProjects={availableProjects}
                    onSave={saveConfig}
                    onClose={() => setNewTabDraft(null)}
                    isCreateMode={true}
                />
            )}

            <OverviewFilterControls
                ref={filterControlsRef}
                filters={filtersData.filters}
                settings={selectedSettings}
                defaultSettings={defaultSettings}
                onApply={onCompleteCustomizerDrawer}
            />

        </LoadingIndicator>
    );
};

const OverviewWithSuspense = (): ReactElement => (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<LoadingIndicator isLoading={true} hideWhileLoading={true} isFullscreen={true}><></></LoadingIndicator>}>
            <ScoreInfoModalProvider>
                <Overview />
            </ScoreInfoModalProvider>
        </Suspense>
    </ErrorBoundary>
);

export default OverviewWithSuspense;
